import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, FileText, LogOut, Receipt, Wallet } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import api from "@/lib/api";
import { fmtMXN, fmtNum, monthLabel, shiftMonth } from "./format";
import { CutHero, Delta, KpiCard, SyncBadge } from "./Kpis";
import { HistoryBars, SalesAreaChart } from "./Charts";
import { BranchCard, CortesCaja, DailyCuts, GoalBar, PnlCard, PreopeningStages } from "./Pnl";

export default function InvestorDashboard({ user, documents, onLogout }) {
  const [month, setMonth] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState("all");

  const load = useCallback(async (m) => {
    try {
      const { data } = await api.get("/investor/dashboard", { params: m ? { month: m } : {} });
      setData(data);
      setMonth(data.month);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "No se pudo cargar el dashboard");
    }
  }, []);

  useEffect(() => { load(month); }, [month, load]);
  useEffect(() => {
    const t = setInterval(() => load(month), 30000);
    return () => clearInterval(t);
  }, [month, load]);

  const currentMonth = data?.today?.slice(0, 7);
  const branch = useMemo(() => data?.branches.find((b) => b.branch_id === selected), [data, selected]);
  const view = useMemo(() => {
    if (!data) return null;
    if (branch && !branch.preopening) return { today: branch.today.ventas, todayTickets: branch.today.tickets, week: branch.week.ventas, monthV: branch.month.ventas, delta: branch.prev_month.ventas ? ((branch.month.ventas - branch.prev_month.ventas) / branch.prev_month.ventas) * 100 : null, pnl: branch.month, series: branch.series, goal: branch.monthly_goal, goalPct: branch.goal_progress_pct, cut: branch.cut, prevCut: branch.prev_cut, pcts: [branch], cortes: data.cortes.filter((c) => c.branch_id === branch.branch_id) };
    const t = data.totals;
    return { today: t.today, todayTickets: t.today_tickets, week: t.week, monthV: t.month_ventas, delta: t.delta_pct, pnl: { ventas: t.month_ventas, tickets: t.tickets, cogs: t.cogs, cogs_basis: t.cogs_basis, cogs_real: t.cogs_real, cogs_estimado: t.cogs_estimado, cobertura_costo_pct: t.cobertura_costo_pct, compras: t.compras, compras_n: t.compras_n, transito: t.transito, incidencias_n: t.incidencias_n, margen_estimado_pct: data.branches.filter((b) => !b.preopening)[0]?.month?.margen_estimado_pct, utilidad_bruta: t.utilidad_bruta, gastos: t.gastos, gastos_detalle: t.gastos_detalle, utilidad_neta: t.utilidad_neta }, series: data.series, goal: t.goal_total, goalPct: t.goal_progress_pct, cut: t.cut_total, prevCut: t.prev_cut_total, pcts: data.branches.filter((b) => !b.preopening), cortes: data.cortes };
  }, [data, branch]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-testid="investor-dashboard">
      <header className="border-b border-white/8 bg-[#0A0A0A]/80 backdrop-blur sticky top-0 z-40">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <BrandLogo size={30} />
            <div>
              <div className="font-display text-sm">Expansión Rubí</div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-[#FFBF57]">Dashboard · Inversionista</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden md:block"><SyncBadge lastSync={data?.last_sync} /></div>
            <div className="text-right hidden sm:block">
              <div className="text-xs font-medium">{user.name}</div>
              <div className="text-[10px] text-neutral-500">{user.email}</div>
            </div>
            <button onClick={onLogout} className="text-xs uppercase tracking-widest font-mono text-neutral-400 hover:text-white flex items-center gap-2 border border-white/10 px-3 py-2 rounded-full" data-testid="portal-logout"><LogOut size={12} /> Salir</button>
          </div>
        </div>
      </header>

      <main className="max-w-[1280px] mx-auto px-5 md:px-8 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#FFBF57] font-mono">Hola, {user.name.split(" ")[0]}</div>
            <h1 className="mt-2 font-display text-3xl md:text-4xl leading-tight">Así va tu participación</h1>
            <p className="mt-2 text-sm text-neutral-400 max-w-xl">Datos en vivo desde RubiPOS de las sucursales asignadas a tu contrato. Actualización automática cada minuto.</p>
          </div>
          <div className="flex items-center gap-2" data-testid="inv-month-nav">
            <button onClick={() => setMonth(shiftMonth(month, -1))} disabled={!month || (!!data?.earliest_month && month <= data.earliest_month)} className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center hover:border-white/30 disabled:opacity-30" data-testid="inv-month-prev"><ChevronLeft size={16} /></button>
            <div className="font-mono text-sm min-w-[170px] text-center" data-testid="inv-month-label">{monthLabel(month)}</div>
            <button onClick={() => setMonth(shiftMonth(month, 1))} disabled={!month || month >= currentMonth} className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center hover:border-white/30 disabled:opacity-30" data-testid="inv-month-next"><ChevronRight size={16} /></button>
          </div>
        </div>

        {error && <div className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-6 text-sm text-rose-200" data-testid="inv-error">{error}</div>}
        {!data && !error && <div className="font-mono text-xs uppercase tracking-widest text-neutral-500 py-20 text-center">Cargando datos en vivo…</div>}

        {data && data.branches.length === 0 && (
          <div className="rounded-3xl border border-dashed border-white/15 p-10 text-center" data-testid="inv-empty">
            <Wallet className="mx-auto text-[#FFBF57]" size={28} />
            <div className="font-display text-2xl mt-4">Aún no tienes sucursales asignadas</div>
            <p className="text-sm text-neutral-400 mt-2 max-w-md mx-auto">En cuanto dirección vincule tu contrato a una o más sucursales verás aquí sus ventas, utilidad y tu corte mensual en tiempo real.</p>
          </div>
        )}

        {data && view && data.branches.length > 0 && (
          <>
            <div className="flex flex-wrap gap-2 mb-6" data-testid="inv-branch-filter">
              <Chip active={selected === "all"} onClick={() => setSelected("all")} testId="inv-chip-all">Todas mis sucursales</Chip>
              {data.branches.map((b) => <Chip key={b.branch_id} active={selected === b.branch_id} onClick={() => setSelected(b.branch_id)} testId={`inv-chip-${b.branch_id}`}>{b.name}</Chip>)}
            </div>

            {branch?.preopening ? (
              <section className="rounded-3xl border border-sky-400/20 bg-[#0B0F14] p-7 md:p-9 mb-8" data-testid="inv-preopening-detail">
                <div className="text-[10px] uppercase tracking-widest text-sky-300 font-mono">Sucursal en pre-apertura · {branch.pct}% de participación</div>
                <h2 className="font-display text-3xl mt-3">{branch.name}</h2>
                <p className="text-sm text-neutral-400 mt-2 max-w-xl">{branch.preopening.done} de {branch.preopening.total} etapas completadas{branch.preopening.target_open_date ? ` · apertura estimada ${new Date(`${branch.preopening.target_open_date}T12:00:00`).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })}` : ""}.</p>
                <div className="mt-6"><PreopeningStages stages={branch.preopening.stages} /></div>
              </section>
            ) : (
              <>
                <div className="grid lg:grid-cols-12 gap-4 mb-4">
                  <div className="lg:col-span-7"><CutHero month={data.month} cut={view.cut} prevCut={view.prevCut} pcts={view.pcts} deltaPct={view.delta} /></div>
                  <div className="lg:col-span-5 grid grid-cols-2 gap-4">
                    <KpiCard label="Ventas hoy" value={fmtMXN.format(view.today)} sub={<span className="text-[11px] font-mono text-neutral-500">{fmtNum.format(view.todayTickets)} tickets</span>} testId="inv-kpi-today" />
                    <KpiCard label="Ventas semana" value={fmtMXN.format(view.week)} sub={<span className="text-[11px] font-mono text-neutral-500">Lunes a hoy</span>} testId="inv-kpi-week" />
                    <div className="col-span-2"><KpiCard label={`Ventas ${monthLabel(data.month).split(" ")[0].toLowerCase()}`} value={fmtMXN.format(view.monthV)} sub={<Delta value={view.delta} />} testId="inv-kpi-month" accent /></div>
                  </div>
                </div>

                <div className="grid lg:grid-cols-12 gap-4 mb-4">
                  <div className="lg:col-span-7 rounded-2xl border border-white/8 bg-[#111112] p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">Ventas diarias · {monthLabel(data.month)}</div>
                      <div className="text-[10px] font-mono text-neutral-600">{selected === "all" ? "Consolidado" : branch?.name}</div>
                    </div>
                    <SalesAreaChart series={view.series} today={data.today} />
                    <div className="mt-6"><GoalBar value={view.monthV} goal={view.goal} pct={view.goalPct} /></div>
                  </div>
                  <div className="lg:col-span-5 space-y-4"><PnlCard m={view.pnl} /><DailyCuts series={view.series} today={data.today} /><CortesCaja cortes={view.cortes || []} branches={selected === "all" ? data.branches : [branch]} /></div>
                </div>
              </>
            )}

            <div className="grid lg:grid-cols-12 gap-4 mb-4">
              <div className="lg:col-span-7">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono mb-3">Mis sucursales</div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {data.branches.map((b) => <BranchCard key={b.branch_id} b={b} active={selected === b.branch_id} onClick={() => setSelected(selected === b.branch_id ? "all" : b.branch_id)} />)}
                </div>
              </div>
              <div className="lg:col-span-5 rounded-2xl border border-white/8 bg-[#111112] p-6">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono mb-4">Historial · últimos 6 meses (consolidado)</div>
                <HistoryBars history={data.history} />
                <div className="mt-4 divide-y divide-white/5" data-testid="inv-history-table">
                  {[...data.history].reverse().map((h) => (
                    <div key={h.month} className="flex items-center justify-between py-2 text-xs">
                      <span className="text-neutral-400 font-mono">{monthLabel(h.month)}</span>
                      <span className="text-neutral-500 font-mono tabular-nums">neta {fmtMXN.format(h.utilidad_neta)}</span>
                      <span className="text-[#FFBF57] font-mono tabular-nums">{fmtMXN.format(h.cut)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        <section className="mt-10" data-testid="inv-documents">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono mb-3 flex items-center gap-2"><Receipt size={12} /> Documentación de tu contrato</div>
          {documents?.length ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {documents.map((doc) => (
                <a key={doc.doc_id} href={doc.url.startsWith("http") ? doc.url : `${process.env.REACT_APP_BACKEND_URL}${doc.url}`} target="_blank" rel="noreferrer" className="rounded-2xl border border-white/8 bg-[#111112] p-4 hover:border-[#FFBF57]/50 transition-colors" data-testid={`portal-doc-${doc.doc_id}`}>
                  <div className="flex items-center gap-2 text-[9px] uppercase tracking-widest font-mono text-[#FFBF57]"><FileText size={12} /> {doc.category}</div>
                  <div className="text-sm text-white mt-2">{doc.title}</div>
                  {doc.note && <div className="text-[11px] text-neutral-500 mt-1">{doc.note}</div>}
                </a>
              ))}
            </div>
          ) : <div className="text-xs text-neutral-600 italic">Tu asesor compartirá aquí el contrato AEP y los reportes mensuales.</div>}
        </section>
      </main>
    </div>
  );
}

function Chip({ active, onClick, children, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className={`text-[11px] font-mono uppercase tracking-wider px-4 py-2 rounded-full border transition-colors ${active ? "border-[#FFBF57] text-[#FFBF57] bg-[#FFBF57]/[0.06]" : "border-white/10 text-neutral-400 hover:text-white hover:border-white/25"}`}>{children}</button>
  );
}
